import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, Loader2 } from "lucide-react";
import { CONTACT, SERVICES } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full bg-[#111111] border border-[#27272a] rounded-none px-4 py-3.5 text-[#f4f4f5] placeholder:text-[#5a5a5f] font-light focus:outline-none focus:border-[#ff4500] transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: SERVICES[0].title, message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry sent. Our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", service: SERVICES[0].title, message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36 border-t border-[#27272a] bg-[#0a0a0a]">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#ff4500]">/ 05 — Get in touch</span>
          <h2 className="mt-5 font-heading font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl text-[#f4f4f5]">
            Your trusted<br />testing partner.
          </h2>
          <p className="mt-8 text-[#8e8e93] text-base md:text-lg font-light leading-relaxed max-w-md">
            Tell us about your requirement and we&apos;ll get back with scope, turnaround and a quote.
          </p>

          <div className="mt-12 space-y-4">
            <a
              data-testid="contact-email"
              href={`mailto:${CONTACT.email}`}
              className="group flex items-center gap-4 text-[#f4f4f5] hover:text-[#ff4500] transition-colors duration-300"
            >
              <span className="w-11 h-11 border border-[#27272a] flex items-center justify-center group-hover:border-[#ff4500] transition-colors duration-300">
                <Mail size={18} />
              </span>
              <span className="font-light">{CONTACT.email}</span>
            </a>
            {CONTACT.phones.map((p) => (
              <a
                key={p}
                data-testid={`contact-phone-${p}`}
                href={`tel:${p}`}
                className="group flex items-center gap-4 text-[#f4f4f5] hover:text-[#ff4500] transition-colors duration-300"
              >
                <span className="w-11 h-11 border border-[#27272a] flex items-center justify-center group-hover:border-[#ff4500] transition-colors duration-300">
                  <Phone size={18} />
                </span>
                <span className="font-mono tracking-wide">+91 {p}</span>
              </a>
            ))}
          </div>
        </div>

        <motion.form
          data-testid="inquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="border border-[#27272a] bg-[#111111] p-6 md:p-10 space-y-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8e8e93] block mb-2">Name</label>
              <input data-testid="input-name" className={FIELD} value={form.name} onChange={set("name")} placeholder="Your name" />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8e8e93] block mb-2">Phone</label>
              <input data-testid="input-phone" className={FIELD} value={form.phone} onChange={set("phone")} placeholder="Phone number" />
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8e8e93] block mb-2">Email</label>
            <input data-testid="input-email" type="email" className={FIELD} value={form.email} onChange={set("email")} placeholder="you@company.com" />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8e8e93] block mb-2">Service required</label>
            <select data-testid="select-service" className={FIELD} value={form.service} onChange={set("service")}>
              {SERVICES.map((s) => (
                <option key={s.title} value={s.title} className="bg-[#111111]">{s.title}</option>
              ))}
              <option value="Other" className="bg-[#111111]">Other / Multiple</option>
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8e8e93] block mb-2">Message</label>
            <textarea data-testid="input-message" rows={4} className={`${FIELD} resize-none`} value={form.message} onChange={set("message")} placeholder="Describe your requirement, project & quantity..." />
          </div>

          <button
            data-testid="submit-inquiry-button"
            type="submit"
            disabled={loading}
            className="group w-full flex items-center justify-center gap-3 bg-[#ff4500] text-[#050505] font-mono text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#f4f4f5] transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : (
              <>
                Send Inquiry
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
